import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

export default function LoginPage() {
  const { login, devLogin, isLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDevMode, setIsDevMode] = useState(false);

  const from = (location.state as { from?: Location })?.from?.pathname || '/poweradmin';

  const handleAzureLogin = async () => {
    try {
      await login();
    } catch (error) {
      toast.error('Erreur lors de la connexion');
    }
  };

  const handleDevLogin = async () => {
    try {
      await devLogin();
      navigate(from, { replace: true });
    } catch (error) {
      toast.error('Erreur lors de la connexion developpement');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4 transition-colors">
      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
      >
        {theme === 'dark' ? (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={theme === 'dark' ? '/images/logo-white.png' : '/images/logo-black.png'}
            alt="PowerITI"
            className="h-16 mx-auto mb-4"
          />
          <p className="text-gray-600 dark:text-gray-400 mt-2">Connexion a l'espace d'administration</p>
        </div>

        {/* Login card */}
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 text-center">
            Se connecter
          </h2>

          {/* Azure AD login */}
          <button
            type="button"
            onClick={handleAzureLogin}
            disabled={isLoading}
            className="btn-primary w-full py-3 mb-4"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23" fill="currentColor">
              <path d="M0 0h11v11H0zM12 0h11v11H12zM0 12h11v11H0zM12 12h11v11H12z" />
            </svg>
            Connexion avec Microsoft
          </button>

          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Connectez-vous avec votre compte Microsoft Entra ID professionnel
          </p>

          {/* Dev mode toggle */}
          {import.meta.env.DEV && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setIsDevMode(!isDevMode)}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 w-full text-center"
              >
                {isDevMode ? 'Masquer le mode developpement' : 'Mode developpement'}
              </button>

              {isDevMode && (
                <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                    Mode developpement - Authentification simulee
                  </p>
                  <button
                    type="button"
                    onClick={handleDevLogin}
                    disabled={isLoading}
                    className="btn-secondary w-full"
                  >
                    Connexion developpement
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Security notice */}
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-6">
          <span className="flex items-center justify-center">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Connexion securisee - Toutes les actions sont enregistrees
          </span>
        </p>
      </div>
    </div>
  );
}
